// Shared HTML parsing utilities for document and space imports
// Based on the parsing logic from ViewAndEditUploadedFile.js

import { marked } from "marked";

// Detect whether an uploaded file is markdown based on its filename
export const isMarkdownFile = (fileName = "") => {
    return /\.(md|markdown|mdown|mkd)$/i.test(fileName);
};

// Convert raw markdown text to an HTML string. GitHub Flavored Markdown is
// enabled so tables and task lists from the source document are preserved and
// can be handled by the downstream Quill-compatibility processing steps.
export const markdownToHTML = (markdownText = "") => {
    return marked.parse(markdownText, { gfm: true, breaks: false });
};

// Convert GitHub Flavored Markdown task lists (rendered by marked as
// <ul><li><input type="checkbox"> ...</li></ul>) into the checklist structure
// Quill expects (<ol><li data-list="checked|unchecked">). Mirrors the output of
// processChecklists so imported markdown checkboxes survive the round-trip.
export const processMarkdownTaskLists = (doc) => {
    // marked renders GFM task items as a disabled checkbox input. Depending on
    // whether the source list is "tight" or "loose", the input sits directly in
    // the <li> or nested inside a <p>, so locate every checkbox and climb to its
    // owning list item rather than relying on a fixed parent shape.
    const checkboxes = doc.querySelectorAll('li input[type="checkbox"]');
    const affectedLists = new Set();

    checkboxes.forEach((checkbox) => {
        const li = checkbox.closest('li');
        if (!li) return;

        const isChecked = checkbox.checked || checkbox.hasAttribute('checked');
        li.setAttribute('data-list', isChecked ? 'checked' : 'unchecked');

        // Remove the rendered checkbox input; Quill renders its own UI span.
        checkbox.remove();

        // Loose lists wrap item content in a <p>; Quill list items hold inline
        // content, so unwrap a lone child <p> back into the <li>.
        const onlyChild = li.children.length === 1 ? li.children[0] : null;
        if (onlyChild && onlyChild.tagName.toLowerCase() === 'p') {
            while (onlyChild.firstChild) {
                li.insertBefore(onlyChild.firstChild, onlyChild);
            }
            onlyChild.remove();
        }

        // Quill checklist items begin with a non-editable UI span.
        const uiSpan = doc.createElement('span');
        uiSpan.className = 'ql-ui';
        uiSpan.setAttribute('contenteditable', 'false');
        li.insertBefore(uiSpan, li.firstChild);

        if (li.parentElement && li.parentElement.tagName.toLowerCase() === 'ul') {
            affectedLists.add(li.parentElement);
        }
    });

    // Quill represents checklists with <ol>, so convert any <ul> that now holds
    // checklist items into an <ol> while preserving its children.
    affectedLists.forEach((ul) => {
        const ol = doc.createElement('ol');
        while (ul.firstChild) {
            ol.appendChild(ul.firstChild);
        }
        ul.parentNode.replaceChild(ol, ul);
    });
};

// Helper function to extract filename from image path
export const getFilenameFromPath = (imageSrc) => {
    // Handle paths like: attachments/2628255748/image.jpeg or just image.jpeg
    return imageSrc.split('/').pop().split('?')[0]; // Remove query params too
};

// Helper function to convert file to base64
export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
};

// Helper function to get MIME type
export const getMimeType = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    const mimeTypes = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'bmp': 'image/bmp',
        'svg': 'image/svg+xml',
        'webp': 'image/webp',
        'tiff': 'image/tiff',
        'tif': 'image/tiff'
    };
    return mimeTypes[extension] || 'image/jpeg';
};

// Process images and replace src attributes
export const processImages = async (doc, attachments) => {
    const images = doc.querySelectorAll('img');
    
    let processedCount = 0;
    let matchedCount = 0;
    
    for (const img of images) {
        const originalSrc = img.getAttribute('src');
        if (!originalSrc) continue;
        
        processedCount++;
        const filename = getFilenameFromPath(originalSrc);
        
        // Find matching attachment by filename
        const matchingAttachment = attachments.find(file => {
            // Handle both File objects directly and objects with name/content structure
            const fileName = file.name || (file.content && file.content.name) || '';
            return fileName === filename || 
                   fileName.toLowerCase() === filename.toLowerCase();
        });
        
        if (matchingAttachment) {
            try {
                let base64;
                
                // Handle different attachment formats
                if (matchingAttachment instanceof File) {
                    // Direct File object (single document import)
                    base64 = await fileToBase64(matchingAttachment);
                } else if (matchingAttachment.content instanceof File) {
                    // Object with File content (space import)
                    base64 = await fileToBase64(matchingAttachment.content);
                } else if (typeof matchingAttachment.content === 'string') {
                    // Object with string content (space import)
                    const mimeType = getMimeType(matchingAttachment.name);
                    if (matchingAttachment.content.startsWith('data:')) {
                        base64 = matchingAttachment.content;
                    } else {
                        base64 = `data:${mimeType};base64,${matchingAttachment.content}`;
                    }
                } else {
                    console.warn(`Unexpected attachment format for ${filename}:`, matchingAttachment);
                    continue;
                }
                
                img.setAttribute('src', base64);
                matchedCount++;
                const attachmentName = matchingAttachment.name || matchingAttachment.content?.name || filename;
            } catch (error) {
                console.error(`Error converting ${filename} to base64:`, error);
            }
        } else {
            console.debug(`✗ No matching attachment found for: ${filename}`);
        }
    }
    return { processedCount, matchedCount };
};

// Process tables to make them compatible with Quill
export const processTables = (doc) => {
    const allTables = doc.querySelectorAll('table');
    
    allTables.forEach((table, index) => {
        const rows = Array.from(table.querySelectorAll('tr'));
        const tableData = [];
        
        rows.forEach((row, rowIndex) => {
            const cells = Array.from(row.querySelectorAll('th, td'));
            const rowData = [];
            
            cells.forEach((cell, cellIndex) => {
                // Extract text content and clean it up
                let cellText = cell.textContent || cell.innerText || '';
                cellText = cellText.trim();
                
                // If cell is empty, use a space
                if (!cellText) {
                    cellText = ' ';
                }
                
                rowData.push({
                    text: cellText,
                    isHeader: cell.tagName.toLowerCase() === 'th',
                    colspan: cell.getAttribute('colspan') || '1',
                    rowspan: cell.getAttribute('rowspan') || '1'
                });
            });
            
            if (rowData.length > 0) {
                tableData.push(rowData);
            }
        });
        
        // Create a new, clean table structure that Quill can work with
        const newTable = document.createElement('table');
        
        // Add tbody (Quill table-better expects tbody)
        const tbody = document.createElement('tbody');
        newTable.appendChild(tbody);
        
        // Build the rows
        tableData.forEach((rowData, rowIndex) => {
            const tr = document.createElement('tr');
            
            rowData.forEach((cellData, cellIndex) => {
                const cell = document.createElement(cellData.isHeader ? 'th' : 'td');
                
                // Set colspan/rowspan if needed
                if (cellData.colspan !== '1') {
                    cell.setAttribute('colspan', cellData.colspan);
                }
                if (cellData.rowspan !== '1') {
                    cell.setAttribute('rowspan', cellData.rowspan);
                }
                
                // Set cell content as plain text (let Quill handle formatting)
                cell.textContent = cellData.text;
                
                tr.appendChild(cell);
            });
            
            tbody.appendChild(tr);
        });
        
        // Replace the original table with the new clean table
        table.parentNode.replaceChild(newTable, table);
    });
    
    // Remove table wrapper divs that Confluence adds
    const tableWrappers = doc.querySelectorAll('.table-wrap');
    
    tableWrappers.forEach(wrapper => {
        const table = wrapper.querySelector('table');
        if (table && wrapper.parentNode) {
            wrapper.parentNode.insertBefore(table, wrapper);
            wrapper.remove();
        }
    });
};

// Process checklists to make them compatible with Quill
export const processChecklists = (doc) => {
    const confluenceChecklists = doc.querySelectorAll('ul.inline-task-list');
    
    // Process checklists from innermost to outermost to handle nesting correctly
    const processedChecklists = new Set();
    
    const processChecklistItems = (checklist, indentLevel = 0) => {
        if (processedChecklists.has(checklist)) return null;
        processedChecklists.add(checklist);
        
        // Convert ul to ol (Quill uses ol for checklists)
        const newChecklist = doc.createElement('ol');
        
        // Get direct children only (not nested ul children)
        const directChildren = Array.from(checklist.children);
        
        directChildren.forEach(child => {
            if (child.tagName.toLowerCase() === 'li' && child.hasAttribute('data-inline-task-id')) {
                const newItem = doc.createElement('li');
                
                // Set Quill checklist attributes
                newItem.setAttribute('data-list', 'unchecked');
                
                // Add indentation class if needed
                if (indentLevel > 0) {
                    newItem.className = `ql-indent-${indentLevel}`;
                }
                
                // Add the required Quill UI span
                const uiSpan = doc.createElement('span');
                uiSpan.className = 'ql-ui';
                uiSpan.setAttribute('contenteditable', 'false');
                
                // Get the text content from the Confluence span
                const span = child.querySelector('span.placeholder-inline-tasks');
                const textContent = span ? (span.textContent || span.innerText || '') : child.textContent;
                
                // Build the new item: <span class="ql-ui" contenteditable="false"></span>text
                newItem.appendChild(uiSpan);
                newItem.appendChild(doc.createTextNode(textContent));
                
                newChecklist.appendChild(newItem);
            } else if (child.tagName.toLowerCase() === 'ul' && child.classList.contains('inline-task-list')) {
                // Process nested checklist with increased indent level
                const nestedItems = processChecklistItems(child, indentLevel + 1);
                if (nestedItems) {
                    // Add nested items to the current checklist
                    Array.from(nestedItems.children).forEach(nestedItem => {
                        newChecklist.appendChild(nestedItem);
                    });
                }
            }
        });
        
        return newChecklist;
    };
    
    // Process top-level checklists
    confluenceChecklists.forEach((checklist, index) => {
        if (processedChecklists.has(checklist)) return;
        
        // Only process if this is a top-level checklist (not nested)
        const parent = checklist.parentElement;
        const isTopLevel = !parent || !parent.classList.contains('inline-task-list');
        
        if (isTopLevel) {
            const newChecklist = processChecklistItems(checklist, 0);
            if (newChecklist && checklist.parentNode) {
                checklist.parentNode.replaceChild(newChecklist, checklist);
            }
        }
    });
};

// Process Confluence and generic code blocks and convert to Quill code block format
export const processCodeBlocks = (doc) => {
    // 1. Confluence code block containers
    const codePanels = doc.querySelectorAll('div.code.panel, div.code.panel.pdl');
    codePanels.forEach((panel, idx) => {
        const pre = panel.querySelector('pre.syntaxhighlighter-pre');
        if (!pre) return;
        const codeText = pre.textContent || '';
        const lines = codeText.split(/\r?\n/);
        const container = doc.createElement('div');
        container.className = 'ql-code-block-container';
        container.setAttribute('spellcheck', 'false');
        lines.forEach(line => {
            const codeLine = doc.createElement('div');
            codeLine.className = 'ql-code-block';
            codeLine.innerHTML = line
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            container.appendChild(codeLine);
        });
        panel.parentNode.replaceChild(container, panel);
    });

    // 2. Generic <pre><code>...</code></pre> blocks
    const preCodeBlocks = doc.querySelectorAll('pre > code');
    preCodeBlocks.forEach((codeElem, idx) => {
        const pre = codeElem.parentElement;
        // Avoid if already inside a Quill code block container
        if (pre.closest('.ql-code-block-container')) return;
        const codeText = codeElem.textContent || '';
        const lines = codeText.split(/\r?\n/);
        const container = doc.createElement('div');
        container.className = 'ql-code-block-container';
        container.setAttribute('spellcheck', 'false');
        lines.forEach(line => {
            const codeLine = doc.createElement('div');
            codeLine.className = 'ql-code-block';
            codeLine.innerHTML = line
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            container.appendChild(codeLine);
        });
        pre.parentNode.replaceChild(container, pre);
    });

    // 3. Standalone <pre> blocks (not already handled)
    const preBlocks = doc.querySelectorAll('pre');
    preBlocks.forEach((preElem) => {
        // Skip if already handled above or inside a Quill code block container
        if (preElem.querySelector('code')) return;
        if (preElem.closest('.ql-code-block-container')) return;
        const codeText = preElem.textContent || '';
        const lines = codeText.split(/\r?\n/);
        const container = doc.createElement('div');
        container.className = 'ql-code-block-container';
        container.setAttribute('spellcheck', 'false');
        lines.forEach(line => {
            const codeLine = doc.createElement('div');
            codeLine.className = 'ql-code-block';
            codeLine.innerHTML = line
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            container.appendChild(codeLine);
        });
        preElem.parentNode.replaceChild(container, preElem);
    });

    // 4. Standalone <code> blocks that are block-level (not inline)
    const codeBlocks = doc.querySelectorAll('code');
    codeBlocks.forEach((codeElem) => {
        // Skip if inside a pre, or inside a Quill code block container, or inside a <p> or <span>
        if (codeElem.closest('pre')) return;
        if (codeElem.closest('.ql-code-block-container')) return;
        if (codeElem.closest('p') || codeElem.closest('span')) return;
        // Only treat as block if it looks like a block (has line breaks or is a direct child of a div)
        const codeText = codeElem.textContent || '';
        if (codeText.indexOf('\n') === -1 && codeElem.parentElement && codeElem.parentElement.childNodes.length === 1) return;
        const lines = codeText.split(/\r?\n/);
        const container = doc.createElement('div');
        container.className = 'ql-code-block-container';
        container.setAttribute('spellcheck', 'false');
        lines.forEach(line => {
            const codeLine = doc.createElement('div');
            codeLine.className = 'ql-code-block';
            codeLine.innerHTML = line
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            container.appendChild(codeLine);
        });
        codeElem.parentNode.replaceChild(container, codeElem);
    });
};

// Remove unwanted elements from HTML
export const removeUnwantedElements = (doc) => {
    // Remove title elements since user already provided the title
    const titleSelectors = [
        '#title-heading',
        '#title-text', 
        '.pagetitle',
        'h1#title-heading',
        '.page-title'
    ];
    
    titleSelectors.forEach(selector => {
        const titleElement = doc.querySelector(selector);
        if (titleElement) {
            titleElement.remove();
        }
    });
    
    // Remove breadcrumbs and navigation elements
    const navigationSelectors = [
        '#breadcrumb-section',
        '#breadcrumbs',
        '#main-header',
        '.page-metadata',
        '#footer'
    ];
    
    navigationSelectors.forEach(selector => {
        const navElement = doc.querySelector(selector);
        if (navElement) {
            navElement.remove();
        }
    });

    // Remove empty paragraphs and divs that create unnecessary spacing
    const emptyElements = doc.querySelectorAll('p:empty, div:empty, span:empty, h1:empty, h2:empty, h3:empty, h4:empty, h5:empty, h6:empty');
    emptyElements.forEach(element => {
        element.remove();
    });

    // Remove elements that only contain whitespace
    const whitespaceOnlyElements = doc.querySelectorAll('p, div, span');
    whitespaceOnlyElements.forEach(element => {
        if (element.textContent && element.textContent.trim() === '' && element.children.length === 0) {
            element.remove();
        }
    });
};

// Main HTML parsing function that combines all processing steps
export const parseHTMLFile = async (file, attachments = []) => {
    try {
        const text = await file.text();

        // Markdown files are converted to HTML up front, then run through the
        // exact same Quill-compatibility pipeline as imported HTML documents.
        const isMarkdown = isMarkdownFile(file?.name);
        const html = isMarkdown ? markdownToHTML(text) : text;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Remove unwanted elements
        removeUnwantedElements(doc);

        // Process code blocks (Confluence to Quill)
        processCodeBlocks(doc);

        // Process tables
        processTables(doc);

        // Process checklists
        processChecklists(doc);

        // Convert markdown (GFM) task lists into Quill checklist format
        if (isMarkdown) {
            processMarkdownTaskLists(doc);
        }
        
        // Process images if attachments are provided
        if (attachments && attachments.length > 0) {
            await processImages(doc, attachments);
        }
        
        // Extract just the main content
        const mainContent = doc.querySelector('#main-content, .wiki-content, .content, main');
        
        let finalContent = '';
        if (mainContent) {
            finalContent = mainContent.innerHTML;
        } else {
            // Fallback: use body content but remove common unwanted elements
            const body = doc.querySelector('body');
            if (body) {
                finalContent = body.innerHTML;
            } else {
                finalContent = text;
            }
        }
        
        return finalContent;
        
    } catch (error) {
        console.error("Error parsing HTML file:", error);
        throw error;
    }
};

// Parse HTML content from string (for space imports)
export const parseHTMLContent = async (htmlContent, attachments = []) => {
    try {
        if (!htmlContent) return '';
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        
        // Remove unwanted elements
        removeUnwantedElements(doc);
        
        // Process code blocks (Confluence to Quill)
        processCodeBlocks(doc);
        
        // Process tables
        processTables(doc);
        
        // Process checklists
        processChecklists(doc);
        
        // Process images if attachments are provided
        if (attachments && attachments.length > 0) {
            await processImages(doc, attachments);
        }
        
        // Extract just the main content
        const mainContent = doc.querySelector('#main-content, .wiki-content, .content, main');
        
        let finalContent = '';
        if (mainContent) {
            finalContent = mainContent.innerHTML;
        } else {
            // Fallback: use body content but remove common unwanted elements
            const body = doc.querySelector('body');
            if (body) {
                finalContent = body.innerHTML;
            } else {
                finalContent = htmlContent;
            }
        }
        
        return finalContent;
        
    } catch (error) {
        console.error("Error parsing HTML content:", error);
        throw error;
    }
};
